import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Upload, InputNumber, Button, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { getDomains, createExploitation, calculateTotalSurface, checkKMLOverlap } from '../../Utils/exploitations/exploitations';
import { getAllProducts } from '../../Utils/products/products';
import { getAllClientsReduced } from '../../Utils/client/client';
import Header from '../../components/Shared/Header';
import backIcon from "../../assets/icons/backIcon.svg";
import { Link, useNavigate } from 'react-router-dom';
import CustomDatePicker from '../../components/Shared/CustomDatePicker';
import { getRoles } from '../../Utils/client/client';


const { Option } = Select;

const ExplCreationPage = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [clients, setClients] = useState([]);
    const [domains, setDomains] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalSurface, setTotalSurface] = useState(0);
    const [kmlFiles, setKmlFiles] = useState([]);
    const [overlapError, setOverlapError] = useState(null);
    const [ignoreOverlap, setIgnoreOverlap] = useState(false);
    const [roles, setRoles] = useState([]);


    useEffect(() => {
        fetchClients();
        fetchProducts();
        fetchRoles();
    }, []);

    const fetchClients = async () => {
        setLoading(true);
        try {
            const clientsData = (await getAllClientsReduced()).data;
            
            setClients(Array.isArray(clientsData) ? clientsData : []);
        } catch (error) {
            console.error('Error fetching clients:', error);
            message.error('Failed to fetch clients');
        } finally {
            setLoading(false);
        }
    };


    const fetchRoles = async () => {
        setLoading(true);
        try {
            const rolesData = await getRoles().then((res) => res.data);
            setRoles(Array.isArray(rolesData) ? rolesData : []);
        } catch (error) {
            console.error('Error fetching roles:', error);
            message.error('Failed to fetch roles');
        } finally {
            setLoading(false);
        }
    };

    const fetchDomains = async (clientId) => {
        setLoading(true);
        try {
            const domainsData = await getDomains(clientId);
            setDomains(Array.isArray(domainsData) ? domainsData : []);
        } catch (error) {
            console.error('Error fetching domains:', error);
            message.error('Failed to fetch domains');
        } finally {
            setLoading(false);
        }
    };

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const productsData = await getAllProducts();
            setProducts(Array.isArray(productsData.data) ? productsData.data : []);
        } catch (error) {
            console.error('Error fetching products:', error);
            message.error('Failed to fetch products');
        } finally {
            setLoading(false);
        }
    };

    const handleClientChange = (value) => {
        fetchDomains(value);
        form.setFieldsValue({ domain_id: undefined });
    };

    const handleKMLUpload = async (info) => {
        const newFiles = info.fileList.map(file => file.originFileObj);
        setKmlFiles(newFiles);

        if (newFiles.length > 0) {
            try {
                const calculatedSurface = await calculateTotalSurface(newFiles);
                const surfaceBrut = Number(calculatedSurface) || 0;
                setTotalSurface(surfaceBrut);
                form.setFieldsValue({ surfaceBrut });

                // Check for overlaps using the latest files
                const overlapResult = await checkKMLOverlap(newFiles);
                if (overlapResult.hasOverlap) {
                    const errorMessage = `Chevauchement détecté dans les fichiers: ${overlapResult.overlappingFiles.join(', ')}`;
                    setOverlapError(errorMessage);
                } else {
                    setOverlapError(null);
                }
            } catch (error) {
                console.error('Error processing KML files:', error);
                message.error('Failed to process KML files');
                setTotalSurface(0);
                setOverlapError('Erreur lors du traitement des fichiers KML');
            }
        } else {
            setTotalSurface(0);
            setOverlapError(null);
        }

        // Trigger real-time validation on the 'files' field
        form.validateFields(['files']);
    };

    useEffect(() => {
        form.validateFields(['files']);
    }, [overlapError, ignoreOverlap]);

    const verifySurface = (_, value) => {
        const netSurface = Number(value);
        if (isNaN(netSurface)) {
            return Promise.reject('La superficie nette doit être un nombre valide.');
        }
        if (netSurface > totalSurface) {
            return Promise.reject('La superficie nette ne peut pas être supérieure à la superficie brute.');
        }
        return Promise.resolve();
    };

    const validateKMLFiles = () => {
        if (overlapError) {
            if (ignoreOverlap) {
                return Promise.resolve();
            }
            return Promise.reject(overlapError);
        }
        return Promise.resolve();
    };


    const onFinish = async (values) => {
        const formData = new FormData();
        formData.append('exploitation_name', values.exploitation_name);
        formData.append('manager', values.manager);
        formData.append('email', values.email); // Add email to formData
        formData.append('exploitiation_superficie_net', values.exploitation_superficie_net);
        if (values.creation_date){
            formData.append('creation_date', values.creation_date.toISOString());
        }
        if (values.installation_date){
            formData.append('installation_date', values.installation_date.toISOString());
        }
        formData.append('region', values.region);
        formData.append('city', values.city);
        formData.append('domain_id', values.domain_id);
        formData.append('user_id', values.user_id);
        formData.append('product_id', Number(values.product_id));
        formData.append('password', values.password);
        kmlFiles.forEach(file => formData.append('files', file));

        try {
            await createExploitation(formData);
            message.success('Exploitation created successfully');
            form.resetFields();
            setTotalSurface(0);
            setKmlFiles([]);
            navigate('/admin/exploitation/all');
        } catch (error) {
            console.error('Error creating exploitation:', error);
            message.error('Failed to create exploitation');
        }
    };


    const handleValueChange = (changedValues, allValues) => {
        if (changedValues.surfaceBrut) {
            setTotalSurface(changedValues.surfaceBrut);
        };
    };

    const clientRoleId = roles.find(role => role.name === 'client')?.role_id;

    return (
        <div className="bg-white h-full">
            <Header title="Création d'exploitation" />
            <div className="h-[calc(100%-100px)] p-6">
                <Link
                    to="/admin/exploitation/all"
                    className="cursor-pointer flex pl-5 gap-2 mt-8"
                >
                    <img src={backIcon} alt="" className="w-5" />{" "}
                    <div className="font-semibold text-base text-indigo-600">
                        {" "}
                        Retour
                    </div>
                </Link>
                <div className='flex justify-center'>
                    <Form form={form} onFinish={onFinish} layout="vertical" className='w-full lg:w-2/3' onValuesChange={handleValueChange}>
                        <Form.Item
                            name="exploitation_name"
                            label="Nom d'exploitation"
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item
                            name="manager"
                            label="Gérant"
                        >
                            <Input />
                        </Form.Item>


                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                                { required: true, message: 'L\'email est requis.' },
                                { type: 'email', message: 'Veuillez entrer un email valide.' }
                            ]}
                        >
                            <Input />
                        </Form.Item>


                        <Form.Item
                            name="user_id"
                            label="Client"
                            rules={[{ required: true, message: 'Le client est requis.' }]}
                        >
                            <Select
                                loading={loading}
                                showSearch
                                placeholder="Sélectionnez un client"
                                optionFilterProp="children"
                                onChange={handleClientChange}
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {clients.map(client => {

                                    // allow only clients with role 'client'
                                    if (client.role_id !== clientRoleId) return null;
                                    return (
                                    <Option key={client.user_id} value={client.user_id}>
                                        {client.company_name}
                                    </Option>
                                )
                                })}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="domain_id"
                            label="Domaine"
                            rules={[{ required: true, message: 'Le domaine est requis.' }]}
                        >
                            <Select loading={loading} placeholder="Sélectionnez un domaine">
                                {domains.map(domain => (
                                    <Option key={domain.domain_id} value={domain.domain_id}>{domain.domain_name}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="region"
                            label="Région"
                        >
                            <Input />
                        </Form.Item>

                        <Form.Item
                            name="city"
                            label="Ville"
                        >
                            <Input />
                        </Form.Item>


                        <Form.Item
                            name="password"
                            label="Mot de passe"
                            rules={[
                                { required: true, message: 'Le mot de passe est requis.' },
                                { min: 8, message: 'Le mot de passe doit contenir au moins 8 caractères.' }
                            ]}
                            hasFeedback
                        >
                            <Input.Password autoComplete="new-password"  />
                        </Form.Item>

                        <Form.Item
                            name="confirm_password"
                            label="Confirmer le mot de passe"
                            dependencies={['password']}
                            hasFeedback
                            rules={[
                                { required: true, message: 'Veuillez confirmer votre mot de passe.' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Les deux mots de passe ne correspondent pas.'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password autoComplete="new-password"  />
                        </Form.Item>

                        <Form.Item
                            name="creation_date"
                            label="Date de création"
                            rules={[{ required: true, message: 'La date de création est requise.' }]}
                        >
                            <CustomDatePicker />
                        </Form.Item>

                        <Form.Item
                            name="installation_date"
                            label="Date d'installation"
                        >
                            <CustomDatePicker />
                        </Form.Item>


                        <Form.Item
                            name="files"
                            label="Fichiers KML"
                            rules={[
                                { validator: validateKMLFiles }
                            ]}
                        >
                            <div className="flex justify-between">
                                <Upload
                                    accept=".kml"
                                    beforeUpload={() => false}
                                    onChange={handleKMLUpload}
                                    multiple
                                >
                                    <Button
                                        icon={<UploadOutlined />}
                                        className="bg-blue-500 hover:bg-blue-600 text-white"
                                    >
                                        Cliquez pour télécharger les fichiers KML
                                    </Button>
                                </Upload>

                                {overlapError &&
                                    <div className="border border-gray-300 h-fit rounded-md px-4 bg-gray-50">
                                        <label className="inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={ignoreOverlap}
                                                onChange={() => setIgnoreOverlap(!ignoreOverlap)}
                                                className="form-checkbox h-5 w-5 text-blue-600 transition duration-150 ease-in-out"
                                            />
                                            <span className="p-2 text-lg font-medium text-gray-700">
                                                Ignorer le chevauchement
                                            </span>
                                        </label>
                                    </div>}
                            </div>
                        </Form.Item>

                        <Form.Item
                            name="surfaceBrut"
                            label="Superficie brute (en ha)"
                        >
                            <InputNumber className='w-full' />
                        </Form.Item>

                        <Form.Item
                            name="exploitation_superficie_net"
                            label="Superficie nette (en ha)"
                            rules={[
                                { validator: verifySurface }
                            ]}
                        >
                            <InputNumber className='w-full' />
                        </Form.Item>

                        <Form.Item
                            name="product_id"
                            label="Produit"
                            rules={[{ required: true, message: 'Le produit est requis.' }]}
                        >
                            <Select
                                loading={loading}
                                showSearch
                                placeholder="Sélectionnez un produit"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {products.map(product => (
                                    <Option key={product.product_id} value={product.product_id}>{product.product_name}</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        

                        <Form.Item>
                            <Button type="primary" htmlType="submit">
                                Soumettre
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default ExplCreationPage;